/**
 * All Open Orders table column definitions and row mapping helpers.
 * Mirrors the My PA Orders schema plus an "Assigned To" column, since this
 * worklist shows orders across every assignee. DOS and creation dates from
 * `_iso` fields use {@link formatDateToUTCWithFormat} so calendar days match
 * UTC-backed API values in every viewer timezone.
 */
import { getQueryCommentsForMedicalPaOrder } from "../../../../api/firebase/firestoreService";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { HOLD_TEXT_UNTIL } from "../../../../constants/stringLiterals";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { Hit } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { MedicalPaOrderStatus } from "../../../../enums/medicalPaOrderStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { MyPaOrdersTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../utils/stringModifications";
import { getOrderIcons } from "../../medicalPaOrdersAlgolia/utils/getOrderIcons";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Column definitions for the All Open Orders table. Widths sum to 100% and
 * include an "Assigned To" column (funded by trimming several data columns).
 */
export const AllOpenOrdersTableColumns: TableHeader[] = [
  {
    label: "Patient Name",
    key: MyPaOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
  },
  {
    label: "Date of Creation",
    key: MyPaOrdersTableColumnKeys.CREATED_AT,
    order: 2,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
  },
  {
    label: "Plan Details",
    key: MyPaOrdersTableColumnKeys.PLAN_DETAILS,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
  },
  {
    label: "Medication",
    key: MyPaOrdersTableColumnKeys.MEDICATION,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
  },
  {
    label: "Provider",
    key: MyPaOrdersTableColumnKeys.PROVIDER,
    order: 5,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
  },
  {
    label: "DOS",
    key: MyPaOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
  },
  {
    label: "BO Value",
    key: MyPaOrdersTableColumnKeys.BO_VALUE,
    order: 7,
    width: 9,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MyPaOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 9,
    type: TableCellType.BADGE_WITH_I,
  },
  {
    label: "Assigned To",
    key: MyPaOrdersTableColumnKeys.ASSIGNEE,
    order: 9,
    width: 10,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 3,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
    order: 12,
    width: 5,
    type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.SIDE_MODAL_ICON,
    order: 13,
    width: 3,
    type: TableCellType.SIDE_MODAL_ICON,
  },
];

/**
 * Returns the All Open Orders table headers.
 *
 * @returns Header array whose widths sum to 100% before review-mode adjustment
 */
export const getAllOpenOrdersTableHeaders = (): TableHeader[] =>
  AllOpenOrdersTableColumns;

/**
 * Transforms raw Postgres hit records into table row objects consumable by CustomTable.
 * Maps patient details, dates, plan info, medication, provider, assignee name, auth
 * status badges, RPA statuses, alerts, and review/order icons into the All Open Orders
 * column schema.
 *
 * @param algoliaRecords - Array of hit records from the Postgres response
 * @param authStatusOptions - Available auth status options for badge rendering
 * @returns Array of table row data objects keyed by MyPaOrdersTableColumnKeys
 */
export const generateAllOpenOrdersTableData = (
  algoliaRecords: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
): MyMedicalPaOrdersTableData[] => {
  return algoliaRecords.map((algoliaRecord) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      algoliaRecord?.auth_status,
    );

    const textToDisplay = `${HOLD_TEXT_UNTIL} ${formatDateToUTCWithFormat(algoliaRecord?.date_of_hold_until_iso ?? "", "MM/DD/YYYY", "--")}`;

    return {
      [MyPaOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
      },
      [MyPaOrdersTableColumnKeys.DATE_OF_SERVICE]: formatDateToUTCWithFormat(
        algoliaRecord?.date_of_service_iso ?? "",
        "MM/DD/YYYY",
        "",
      ),
      [MyPaOrdersTableColumnKeys.PLAN_DETAILS]: {
        mainText: algoliaRecord?.primary_payer_name ?? "",
        secondaryText: algoliaRecord?.primary_member_id ?? "",
      },
      [MyPaOrdersTableColumnKeys.MEDICATION]: algoliaRecord?.regimen_name ?? "",
      [MyPaOrdersTableColumnKeys.ASSIGNEE]:
        algoliaRecord?.assigned_to_name || "-",
      [MyPaOrdersTableColumnKeys.ROW_DATA]: JSON.stringify(algoliaRecord),
      [MyPaOrdersTableColumnKeys.ID]: algoliaRecord?.id ?? "",
      [MyPaOrdersTableColumnKeys.CREATED_AT]: formatDateToUTCWithFormat(
        algoliaRecord?.created_at_iso ?? "",
        "MM/DD/YYYY",
        "",
      ),
      [MyPaOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === MedicalPaOrderStatus.HOLD ||
          authStatus?.id === MedicalPaOrderStatus.QUERY,
        displayText: textToDisplay,
        fetchFn:
          authStatus?.id === MedicalPaOrderStatus.QUERY
            ? () => getQueryCommentsForMedicalPaOrder(algoliaRecord?.id ?? "")
            : undefined,
      },
      [MyPaOrdersTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        algoliaRecord?.practitioner_name ?? "",
      ),
      [MyPaOrdersTableColumnKeys.BO_VALUE]: {
        text: algoliaRecord?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MyPaOrdersTableColumnKeys.LOCATION]: algoliaRecord?.location ?? "",
      [MyPaOrdersTableColumnKeys.RPA_STATUS]: {
        status:
          algoliaRecord?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
      [MyPaOrdersTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          algoliaRecord?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
      [MyPaOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          algoliaRecord?.medical_order_status as MedicalOrderStatus,
          algoliaRecord?.medical_order_review_status as MedicalOrderReviewStatus,
          algoliaRecord?.master_auth_status === "query" &&
            algoliaRecord?.ai_agent_type === "agent",
        ),
        order_id: algoliaRecord?.id ?? "",
      },
      [MyPaOrdersTableColumnKeys.SIDE_MODAL_ICON]: {
        id: algoliaRecord?.id ?? "",
        status: "others",
        borderColor: algoliaRecord?.notes_color ?? "",
        isNotesPresent: algoliaRecord?.notes_present ?? false,
        patientId: algoliaRecord?.patient_id ?? "",
      },
    };
  });
};
